import React, { useMemo } from 'react'
import { Trans } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter, TableAdapter } from '@makyoapp/makyo-common'
import { Column16, Table16 } from '@makyoapp/makyo-icon/icon'
import { SelectDropdown } from '@makyoapp/makyo-ui'

import { SHOW_SEARCH_LIMIT } from '@/shared/constants'

import { RenderColumn } from '../RenderColumn'

export interface TableDataSourceWithColumnDropdownProps {
  tables: TableAdapter[]
  columnTypes?: ColumnTypes[]
  id?: string
  value?: {
    table: string
    columns: string[]
  }
  onChange: (nextValue: { table: string; columns: string[] }) => void
}

export function TableDataSourceWithColumnDropdown({
  tables,
  columnTypes,
  value,
  onChange,
  id,
}: TableDataSourceWithColumnDropdownProps) {
  const tblOptions = useMemo(() => {
    return [{ id: '', name: 'Select Table' } as TableAdapter, ...tables]
  }, [tables])

  const colOptions = useMemo(() => {
    return tables.find((tb) => tb.id === value?.table)?.columns ?? []
  }, [value, tables])

  const filteredColumns = useMemo(() => {
    return columnTypes
      ? colOptions.filter(({ type }) => {
          return columnTypes.includes(type)
        })
      : colOptions
  }, [colOptions, columnTypes])

  return (
    <div className='flex flex-col w-full gap-4'>
      <SelectDropdown
        withSearch={tblOptions.length > SHOW_SEARCH_LIMIT}
        id={`${id}-table`}
        onChange={(tb) => {
          onChange({
            table: tb.id,
            columns: tb.id !== value?.table ? [] : (value?.columns ?? []),
          })
        }}
        value={tblOptions.find((tb) => tb.id === value?.table)}
        optionLabel={(tb) => tb?.name ?? ''}
        options={tblOptions}
        renderOption={(tb) => {
          return (
            <div className='flex-c'>
              <Table16 />
              <div>{tb?.id === '' ? <Trans>{tb?.name}</Trans> : tb?.name}</div>
            </div>
          )
        }}
      />
      <SelectDropdown
        id={`${id}-columns`}
        withSearch={filteredColumns.length > SHOW_SEARCH_LIMIT}
        multiple
        onChange={(col) => {
          onChange({
            table: value?.table ?? '',
            columns: (col as ColumnAdapter[]).map(({ id }) => id),
          })
        }}
        value={filteredColumns.filter((col) => value?.columns.includes(col.id))}
        optionLabel={(col) => `${(col as unknown as ColumnAdapter)?.id} ${(col as unknown as ColumnAdapter)?.name}`}
        options={filteredColumns as never}
        placeholder={
          <div
            className='flex-c'
            style={{
              paddingInlineStart: '.5rem',
            }}
          >
            <Column16 />
            <div>
              <Trans>Select Columns</Trans>
            </div>
          </div>
        }
        renderOption={(opt) => {
          return <RenderColumn withStyle={false} col={opt as unknown as ColumnAdapter} />
        }}
      />
    </div>
  )
}
